/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import filter from 'lodash.filter';
import isNil from 'lodash.isnil';
import {WorkBook} from 'xlsx/types';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {DropsheetOptions, TemplateColumn} from '@ion/core/src/types/templates';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import {
  normalizeAttributes,
  normalizeHeaders,
  process
} from '@ion/core/src/xlsx/ExcelHelpers';
import {
  dropsheet
} from '@ion/core/src/xlsx/importer';
import {ImportParamsType} from '@ion/dls/src/imports/BulkUploadDialog';

/**
 * Hook to encapsulate import functionality for Bulk Add Devices.
 */
export const useBulkAddDevicesImport = (
  deviceIdentifiersRef
) => {
  const isMounted                             = useHasMounted();

  const [importStatus, setImportStatus]       = useState(false);
  const [importComplete, setImportComplete]   = useState(false);
  const [importerOptions, setImporterOptions] = useState<DropsheetOptions>();
  const [importParams, setImportParams]       = useState<ImportParamsType>();
  const templateColumns: TemplateColumn[]     = translate('admin-devices:dialogBulkAdd.template', {returnObjects: true});
  const attributes                            = idMap(templateColumns, 'header', 'key');
  const importResults                         = useSelector(({importer: {current}}) => current);
  const headers                               = Object.keys(attributes);

  /**
   * Runs upon successful completion of the upload of the file containing device identifiers.
   */
  const onImportCompleted = function(
    items: Array<any>,
    workbook: WorkBook,
    files: Array<any>
  ) {
    const file = files ? files[0] : {};
    let errorCheck = false;
    const mandatoryCols = filter(templateColumns, {required: true});
    for (const item of items) {
      for (const col of mandatoryCols) {
        if (isNil(item[col.key])) {
          errorCheck = true;
          break;
        }
      }
      if (errorCheck) break;
    }
    if (errorCheck) return onImportFailed();

    setImportParams({
      ...importParams,
      fileName   : file?.name,
      fileSize   : file?.size,
      isComplete : true,
      isFailure  : false,
      items
    });

    deviceIdentifiersRef.current = items.map(item => item.deviceIdentifier);
  };

  /**
   * Runs upon a failed upload of the file containing device identifiers.
   */
  const onImportFailed = function(e?: unknown) {
    clearImport();
    setImportParams({
      ...importParams,
      isComplete : false,
      isFailure  : true
    });
    deviceIdentifiersRef.current = [];
  };

  const converter = (column: string): string => {
    const attrs   = normalizeAttributes(attributes);
    const extract = column !== headers[5];
    const header  = normalizeHeaders(column, attrs, extract);
    return header || column;
  };

  /**
   * Uploads a file of device identifiers.
   */
  function handleImport(files: FileList) {
    setImportStatus(true);
    process(files, importerOptions, true);
  };

  /**
   * Initializes upload information such as the items uploaded and the upload status.
   */
  const initializeImportParams = function() {
    setImportParams({
      items      : [],
      isComplete : false,
      isFailure  : false
    });
  };

  /**
   * Clears any current import operation.
   */
  const clearImport = function() {
    const importerId = 'device-import';
    const importContainer = document.getElementById(importerId) as HTMLInputElement;
    if (importContainer) {
      importContainer.value = null;
    }
    setImportStatus(false);
    initializeImportParams();
  };

  /**
   * Initializes the upload operational options.
   */
  const initializeImporterOptions = () => {
    setImporterOptions({
      onComplete  : onImportCompleted,
      onFailed    : onImportFailed,
      onSizeError : onImportFailed,
      parse       : true,
      converter
    });
  };

  useEffect(() => {
    if (!isMounted) {
      initializeImporterOptions();
      initializeImportParams();
    } else {
      importerOptions.target = document.getElementById('device-importer');
      dropsheet(importerOptions, setImportStatus, true);
    }
  }, [isMounted]);

  useEffect(() => {
    setImportComplete(importStatus && importParams?.isComplete);
  }, [importStatus, importParams?.isComplete]);

  return {
    templateColumns,
    importParams,
    importStatus,
    importComplete,
    importResults,
    handleImport,
    clearImport
  };
};

export default useBulkAddDevicesImport;
